"use client";

import { BlockiesAvatar } from "./blockies-avatar";
import { QuestLogo } from "./quest-logo";

interface UserStatsProps {
  address?: string;
  name?: string;
  totalPoints: number;
  leaderboardRank: number;
  dailyStreak?: number;
  completedTasks?: number;
  totalTasks?: number;
}

export function UserStats({
  address,
  name,
  totalPoints,
  leaderboardRank,
  completedTasks,
  totalTasks,
}: UserStatsProps) {
  const showCompletedTasks =
    completedTasks !== undefined && totalTasks !== undefined;
  const displayName =
    name ||
    (address ? `${address.slice(0, 6)}...${address.slice(-4)}` : "User");

  return (
    <div className="w-full py-8 md:py-12">
      <div className="w-full max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
        {/* Greeting with Avatar */}
        <div className="flex items-start gap-3 mb-6">
          {address && (
            <BlockiesAvatar
              seed={address}
              size={12}
              scale={4}
              className="rounded-full flex-shrink-0"
            />
          )}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              <span className="text-yiedl-primary">Welcome,</span> {displayName}
              !
            </h2>
            <p className="text-gray-400 text-sm md:text-base">
              Complete tasks. Invite friends. Get rewarded.
            </p>
          </div>
        </div>

        {/* Stats Grid - dynamic columns based on whether completed tasks is shown */}
        <div
          className={`grid grid-cols-1 gap-4 md:gap-6 ${showCompletedTasks ? "md:grid-cols-3" : "md:grid-cols-2"}`}
        >
          {/* Total Points */}
          <div
            className="relative p-6 rounded-2xl overflow-hidden"
            style={{
              backgroundImage:
                "url(https://pulsarmoney.b-cdn.net/quests/campaigns/yield/stats-card.png)",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <p className="text-white text-2xl md:text-3xl font-bold">
                  {totalPoints.toLocaleString()}
                </p>
                <QuestLogo size={20} className="text-yiedl-primary" />
              </div>
              <p className="text-gray-300 text-sm font-medium">Total Points</p>
            </div>
          </div>
          {/* Leaderboard Place */}
          <div
            className="relative p-6 rounded-2xl overflow-hidden"
            style={{
              backgroundImage:
                "url(https://pulsarmoney.b-cdn.net/quests/campaigns/yield/stats-card.png)",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="relative z-10">
              <div className="text-white text-2xl md:text-3xl font-bold mb-3">
                #{leaderboardRank.toLocaleString()}
              </div>
              <p className="text-gray-300 text-sm font-medium">
                Place in Leaderboard
              </p>
            </div>
          </div>

          {/* Completed Tasks - Only shown when props are provided */}
          {showCompletedTasks && (
            <div
              className="relative p-6 rounded-2xl overflow-hidden"
              style={{
                backgroundImage:
                  "url(https://pulsarmoney.b-cdn.net/quests/campaigns/yield/stats-card.png)",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="relative z-10">
                <div className="text-white text-2xl md:text-3xl font-bold mb-3">
                  {completedTasks}/{totalTasks}
                </div>
                <p className="text-gray-300 text-sm font-medium">
                  Completed tasks
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
